import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ChangeDetail } from '../lib/api';
import { dateTime } from '../lib/format';

// Veta Change · M3: evolutivo en Veta para un cambio aprobado de facturación adicional (> 40 h).
// Llega pre-llenado; el admin o el gestor lo revisa y lo confirma con un clic.

const field = 'h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const money = (n: number, c: string) => { try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: c }).format(n); } catch { return `${n} ${c}`; } };

export function EvolutivePanel({ d, onMessage }: { d: ChangeDetail; onMessage: (ok: boolean, text: string) => void }) {
  const ev = d.evolutive!;
  const p = ev.proposal;
  const queryClient = useQueryClient();
  const [f, setF] = useState({ code: p.code, name: p.name, parentId: p.parentId ?? '', maxHours: String(p.maxHours), endDate: p.endDate ?? '' });
  useEffect(() => { setF({ code: p.code, name: p.name, parentId: p.parentId ?? '', maxHours: String(p.maxHours), endDate: p.endDate ?? '' }); }, [p]);

  const confirm = useMutation({
    mutationFn: () => api<{ code: string; moved: { moved: number; hours: number } }>(`/change/${d.ticket.number}/evolutive/confirm`, {
      method: 'POST',
      body: JSON.stringify({ code: f.code, name: f.name, parentId: f.parentId || undefined, maxHours: Number(f.maxHours), endDate: f.endDate || null })
    }),
    onSuccess: r => {
      void queryClient.invalidateQueries({ queryKey: ['change', d.ticket.number] });
      void queryClient.invalidateQueries({ queryKey: ['ticket', d.ticket.number] });
      onMessage(true, `Evolutivo ${r.code} creado en Veta. Se movieron ${r.moved.moved} registros de horas (${r.moved.hours} h).`);
    },
    onError: (e: unknown) => onMessage(false, e instanceof Error ? e.message : 'No se pudo crear el evolutivo.')
  });

  if (ev.status === 'confirmed') {
    return (
      <section className="rounded-lg border border-line bg-white p-4 sm:p-5">
        <h2 className="text-base font-semibold">Evolutivo en Veta</h2>
        <p className="mt-2 text-sm text-[#3F3D39]">
          <span className="font-semibold">{p.code}</span> · {p.name} · cuelga de {p.parentCode ?? '—'} · tope {p.maxHours} h
        </p>
        <p className="mt-1 text-sm text-muted">
          Creado por {ev.confirmedBy?.name ?? '—'} el {dateTime(ev.confirmedAt)}. Horas de estimación movidas al evolutivo: {ev.movedHours ?? 0} h ({ev.movedEntries ?? 0} registros).
          Las horas nuevas del ticket se capturan en el evolutivo.
        </p>
      </section>
    );
  }

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <section className="flex flex-col gap-4 rounded-lg border border-[#F2C98A] bg-[#FFFBF4] p-4 sm:p-5">
      <div>
        <h2 className="text-base font-semibold">Evolutivo propuesto para Veta</h2>
        <p className="mt-1 text-sm text-muted">
          El cliente aprobó y es facturación adicional: no consume horas del contrato. Revisa los datos y confírmalo; Veta crea el evolutivo,
          le pasa las horas de estimación ya capturadas con este ticket y el ticket queda ligado a él.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">Código<input className={field} value={f.code} onChange={set('code')} /></label>
        <label className="flex flex-col gap-1 text-sm">Nombre<input className={field} value={f.name} onChange={set('name')} /></label>
        <label className="flex flex-col gap-1 text-sm">Proyecto padre (soporte)
          <select className={field} value={f.parentId} onChange={set('parentId')}>
            <option value="">Elige…</option>
            {ev.parentOptions.map(o => <option key={o.id} value={o.id}>{o.code} · {o.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">Tope de horas<input className={field} type="number" min="0.5" step="0.5" value={f.maxHours} onChange={set('maxHours')} /></label>
        <label className="flex flex-col gap-1 text-sm">Vigencia hasta<input className={field} type="date" value={f.endDate} onChange={set('endDate')} /></label>
        <div className="flex flex-col gap-1 text-sm">
          <span>Tarifa y consultores</span>
          <span className="text-[#3F3D39]">{money(p.saleRate, p.currency)}/h · {p.assignedNames.join(', ') || '—'}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button className={btnPrimary} disabled={confirm.isPending || !ev.vetaConnected || !f.parentId} onClick={() => confirm.mutate()}>
          {confirm.isPending ? 'Creando en Veta…' : 'Confirmar y crear en Veta'}
        </button>
        {!ev.vetaConnected && <span className="text-sm text-[#8F1D1D]">Falta configurar la conexión con Veta en el servidor.</span>}
        <span className="text-xs text-muted">Propuesto el {dateTime(ev.proposedAt)}</span>
      </div>
    </section>
  );
}
